import React, { useEffect, useContext, useState, useMemo } from "react";
import Footer from "../components/footer";
import { listOrganizationalUnits, getOrganizationalUnit } from "../services/api_org_unit";
import { AuthContext } from "../contexts/auth";
import ComboboxAutocomplete from "../components/combobox_autocomplete";
import Fetching from "../components/fetching";
import CheckPermissions from "../components/check_permissions";
import ListBoxPageSize from "../components/listbox_pase_size";
import { Link } from "wouter";
import TimbraturaWeekTable from "../components/timbratura_week_table";
import PopoverComponentCustom from "../components/popover_component";
import AttendanceControlsBar from "../components/attendance_controls_bar";
import SchedulerContextBar from "../components/scheduler_context_bar";
import moment from "moment";
import TimbraturaMonthTable from "../components/timbratura_month_table";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import ModalExtractionInouts from '../components/modal_extraction_inouts';


// calcola start/end in base alla vista corrente
function getRangeForMode(mode, anchorDate) {
  const d = moment(anchorDate);
  if (mode === "GIORNALIERO") {
    const startISO = d.clone().startOf("day").format("YYYY-MM-DD");
    const endISO   = d.clone().endOf("day").format("YYYY-MM-DD");
    return { startISO, endISO };
  }
  if (mode === "SETTIMANALE") {
    // settimana LUN-DOM
    const startISO = d.clone().startOf("isoWeek").format("YYYY-MM-DD");
    const endISO   = d.clone().endOf("isoWeek").format("YYYY-MM-DD");
    return { startISO, endISO };
  }
  // MENSILE
  const startISO = d.clone().startOf("month").format("YYYY-MM-DD");
  const endISO   = d.clone().endOf("month").format("YYYY-MM-DD");
  return { startISO, endISO };
}
  
function TimbratureCompanyView() {
    const { token, profile } = useContext(AuthContext);
    const [mode, setMode] = useState("SETTIMANALE");
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedUnitId, setSelectedUnitId] = useState(null);
    const [units, setUnits] = useState([]);
    const selectedUnit = useMemo(
        () => units.find(u => u.id === selectedUnitId) || null,
        [units, selectedUnitId]
    );

    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const employeeIds = useMemo(() => selectedEmployees.map(e => e.id), [selectedEmployees]);

    const [unitData, setUnitData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [totalResult, setTotalResult] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);
    const [showModalExportInouts, setShoModalExportInouts] = useState(false);

    const isEmployee = profile.customer_type === 'employee';
    const hasManageJustificationPermission =
        profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
        profile.employee?.permissions?.includes('hr.manage_justification_all');

    const hasViewShiftPermission =
        profile.employee?.permissions?.includes('hr.view_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.view_shifts_all') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_all');
    
    const hasInOutsPermission = 
        profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.view_inouts_all') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_all');

    const isProfileComplete = profile.has_complete_profile;

    const canExportInouts = !isEmployee || hasInOutsPermission || hasViewShiftPermission || hasManageJustificationPermission;

    const { startISO, endISO } = useMemo(
        () => getRangeForMode(mode, currentDate),
        [mode, currentDate]
    );
    

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize); 
        setPage(1); 
    };

    const handleNextPage = () => nextPage && setPage((p) => p + 1);
    const handlePreviousPage = () => prevPage && setPage((p) => p - 1);

    useEffect(() => {
        const fetchUnits = async () => {
        setIsLoading(true);
        setIsError(false);
        try {
            const resp = await listOrganizationalUnits(token, true, null, "shifts");
            const list = resp?.data || [];
            setUnits(list);
            if (!selectedUnitId && list.length > 0) setSelectedUnitId(list[0].id);
        } catch (err) {
            console.error("Error fetching units:", err);
            if (err?.response?.status !== 402) setIsError(true);
        } finally {
            setIsLoading(false);
        }
        };
        fetchUnits();
    }, [token]);

    const handleParentChange = (nextUnit) => {
        if (nextUnit) {
            setSelectedUnitId(nextUnit.id);
        } else {
            setSelectedUnitId(null);
        }
        // quando cambio UO, resetto selezione dipendenti (coerente)
        if (selectedEmployees.length) setSelectedEmployees([]);
        setPage(1);
    };

    const handleResetUnitSelection = () => {
        setSelectedUnitId(null);
        if (selectedEmployees.length) setSelectedEmployees([]);
        setPage(1);
    };

    const handleEmployeesChange = (nextArr) => {
        setSelectedEmployees(nextArr);
        setPage(1);
    };


    if (isLoading) return <Fetching />
    if (isError) return <div>Error loading data</div>;

    return (
        <div className="flex flex-col min-h-screen">
            <PopoverComponentCustom title="Timbrature" />
            {profile?.customer_type === 'company' && !isProfileComplete && profile?.company?.email == profile?.the_real_user_email && (
                <div className="w-full">
                    <div className="bg-orange-100 text-orange-600 p-4 mb-6">
                        <p className="text-center text-sm font-semibold">
                            Il profilo non è completo. <br />
                            Compila i campi mancanti{' '}
                            <Link
                            to="/profilo/fatturazione"
                            className="text-indigo-600 hover:text-indigo-800 underline"
                            >
                            cliccando qui
                            </Link>
                        </p>
                    </div>
                </div>
            )}
            <main className="mt-10 pb-8 flex-grow">
                <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">
                    <CheckPermissions permissionsToCheck={['hr.view_inouts_all', 'hr.view_inouts_ou', 'hr.manage_inouts_all', 'hr.manage_inouts_ou']}>
                        <div className="mb-4">
                            <SchedulerContextBar
                                className="w-full"
                                units={units}
                                selectedUnit={selectedUnit}
                                onSelectUnit={handleParentChange}
                                onResetUnit={handleResetUnitSelection}
                                isUnitsLoading={false}
                                token={token}
                                selectedEmployees={selectedEmployees}
                                onEmployeesChange={handleEmployeesChange}
                                pageSize={pageSize}
                                onPageSizeChange={handlePageSizeChange}
                                contractStartDate={startISO}
                                contractEndDate={endISO}
                            />
                        </div>

                        <div className='col-span-1 flex justify-start sm:justify-end me-4'>
                            <button 
                                className={`text-white text-xs py-1.5 px-2 rounded uppercase font-bold ${
                                    canExportInouts
                                        ? 'bg-indigo-600 hover:bg-indigo-500'
                                        : 'bg-gray-300 cursor-not-allowed'
                                }`}
                                onClick={() => {
                                    if (!canExportInouts) {
                                        Swal.fire({
                                            title: 'Non hai i permessi per eseguire questa azione',
                                            icon: 'warning',
                                            confirmButtonText: 'Chiudi',
                                        });
                                        return;
                                    }
                                    setShoModalExportInouts(true);
                                }}
                            >
                                <ArrowDownTrayIcon className="h-4 w-4 inline-block me-2" />
                                Esporta Timbrature
                            </button>
                        </div>

                        <div className="mb-0">
                            <AttendanceControlsBar
                                className="w-full"
                                mode={mode}
                                onModeChange={setMode}
                                date={currentDate}
                                onDateChange={setCurrentDate}
                                hideDailyTab
                                paddingClass="pt-2"
                            />
                        </div>

                        {mode === 'SETTIMANALE' ? (
                            <TimbraturaWeekTable
                                selectedUnit={selectedUnit}
                                employeeIds={employeeIds}
                                page={page}
                                totalResult={totalResult}
                                setTotalResult={setTotalResult}
                                pageSize={pageSize}
                                setNextPage={setNextPage}
                                setPrevPage={setPrevPage}
                                startDate={startISO}
                                endDate={endISO}
                            />
                        ) : (
                            <TimbraturaMonthTable
                                selectedUnit={selectedUnit}
                                employeeIds={employeeIds}
                                page={page}
                                totalResult={totalResult}
                                setTotalResult={setTotalResult}
                                pageSize={pageSize}
                                setNextPage={setNextPage}
                                setPrevPage={setPrevPage}
                                startDate={startISO}
                                endDate={endISO}
                            />
                        )}
                    </CheckPermissions>
              
                    {totalResult > pageSize && (
                        <div className="flex justify-between mt-8 px-8">
                            <button 
                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                onClick={handlePreviousPage}
                                disabled={!prevPage}
                            >
                                Precedente
                            </button>
                            <span className='text-sm font-medium text-gray-900'>Pagina {page} di {Math.ceil(totalResult / pageSize)}</span>
                            <button 
                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                onClick={handleNextPage} 
                                disabled={!nextPage}
                            >
                                Successivo
                            </button>
                        </div>
                    )}
                    {showModalExportInouts && (
                        <ModalExtractionInouts
                            show={showModalExportInouts}
                            onClose={() => setShoModalExportInouts(false)}
                            selectedUnit={selectedUnit}
                            selectedEmployees={selectedEmployees}
                            defaultStartDate={startISO}
                            defaultEndDate={endISO} 
                        />
                    )}
                </div>
            </main>
            <Footer />
        </div>
    )
};

export default TimbratureCompanyView;
